import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../App';
import { toast } from 'sonner';
import { Lock, Truck, Tag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Checkout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cart, cartTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoApplied, setPromoApplied] = useState(false);
  
  const [form, setForm] = useState({
    email: '',
    phone: '',
    firstName: '',
    lastName: '',
    address: '',
    apartment: '',
    city: '',
    state: '',
    zip: '',
    country: 'United States'
  });

  const shipping = cartTotal >= 75 ? 0 : 5.99;
  const tax = cartTotal * 0.08;
  const discount = promoApplied ? (cartTotal * promoDiscount / 100) : 0;
  const total = cartTotal + shipping + tax - discount;

  const handlePromoCode = async () => {
    if (!promoCode.trim()) return;
    try {
      const res = await fetch(`${API}/promo/validate?code=${promoCode}`);
      if (res.ok) {
        const data = await res.json();
        setPromoDiscount(data.discount_percent);
        setPromoApplied(true);
        toast.success(`Promo code applied! ${data.discount_percent}% off`);
      } else {
        toast.error('Invalid promo code');
      }
    } catch (error) {
      toast.error('Error validating promo code');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) { toast.error('Your cart is empty'); return; }
    setLoading(true);
    try {
      const orderRes = await fetch(`${API}/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cart,
          shipping_address: {
            firstName: form.firstName, lastName: form.lastName,
            address: form.address, apartment: form.apartment,
            city: form.city, state: form.state, zip: form.zip, country: form.country
          },
          email: form.email, phone: form.phone,
          promo_code: promoApplied ? promoCode : null
        })
      });
      if (!orderRes.ok) throw new Error('Failed to create order');
      const order = await orderRes.json();

      const checkoutRes = await fetch(`${API}/checkout/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: order.id, origin_url: window.location.origin })
      });
      if (!checkoutRes.ok) throw new Error('Failed to create checkout session');
      const { url } = await checkoutRes.json();
      window.location.href = url;
    } catch (error) {
      // Checkout error
      toast.error('Error processing checkout. Please try again.');
      setLoading(false);
    }
  };

  if (cart.length === 0) { navigate('/cart'); return null; }

  const inputClass = "w-full bg-cs-bg border border-cs-border text-cs-text font-mono text-sm p-3 focus:outline-none focus:border-cs-ochre transition-colors placeholder:text-cs-muted/50";
  const labelClass = "font-mono text-sm text-cs-text uppercase tracking-wider block mb-2";

  return (
    <div className="fade-in" data-testid="checkout-page">
      <section className="bg-cs-dark border-b border-cs-border py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-mono text-xs text-cs-ochre uppercase tracking-[0.3em] mb-2">Secure Checkout</p>
          <h1 className="font-anton text-4xl md:text-5xl uppercase text-cs-text tracking-wider" data-testid="checkout-title">Checkout</h1>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              {/* Contact Info */}
              <div className="bg-cs-card border border-cs-border p-6">
                <h2 className="font-anton text-xl uppercase text-cs-text mb-6 tracking-wide">Contact Information</h2>
                <div className="space-y-4">
                  <div>
                    <label className={labelClass}>Email <span className="text-cs-alert">*</span></label>
                    <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required className={inputClass} placeholder="your@email.com" data-testid="checkout-email" />
                  </div>
                  <div>
                    <label className={labelClass}>Phone (optional)</label>
                    <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputClass} placeholder="(555) 123-4567" data-testid="checkout-phone" />
                  </div>
                </div>
              </div>

              {/* Shipping */}
              <div className="bg-cs-card border border-cs-border p-6">
                <h2 className="font-anton text-xl uppercase text-cs-text mb-6 tracking-wide flex items-center gap-2">
                  <Truck className="w-5 h-5 text-cs-ochre" /> Shipping Address
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>First Name <span className="text-cs-alert">*</span></label>
                    <input type="text" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} required className={inputClass} data-testid="checkout-firstname" />
                  </div>
                  <div>
                    <label className={labelClass}>Last Name <span className="text-cs-alert">*</span></label>
                    <input type="text" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} required className={inputClass} data-testid="checkout-lastname" />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelClass}>Address <span className="text-cs-alert">*</span></label>
                    <input type="text" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} required className={inputClass} placeholder="Street address" data-testid="checkout-address" />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelClass}>Apartment, suite, etc.</label>
                    <input type="text" value={form.apartment} onChange={e => setForm({ ...form, apartment: e.target.value })} className={inputClass} data-testid="checkout-apartment" />
                  </div>
                  <div>
                    <label className={labelClass}>City <span className="text-cs-alert">*</span></label>
                    <input type="text" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} required className={inputClass} data-testid="checkout-city" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>State <span className="text-cs-alert">*</span></label>
                      <input type="text" value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} required className={inputClass} data-testid="checkout-state" />
                    </div>
                    <div>
                      <label className={labelClass}>ZIP <span className="text-cs-alert">*</span></label>
                      <input type="text" value={form.zip} onChange={e => setForm({ ...form, zip: e.target.value })} required className={inputClass} data-testid="checkout-zip" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Promo Code */}
              <div className="bg-cs-card border border-cs-border p-6">
                <h2 className="font-anton text-xl uppercase text-cs-text mb-4 tracking-wide flex items-center gap-2">
                  <Tag className="w-5 h-5 text-cs-ochre" /> Promo Code
                </h2>
                <div className="flex gap-3">
                  <input type="text" value={promoCode} onChange={e => setPromoCode(e.target.value.toUpperCase())} placeholder="Enter code" disabled={promoApplied} className={`${inputClass} flex-1`} data-testid="promo-code-input" />
                  <button type="button" onClick={handlePromoCode} disabled={promoApplied} className="border border-cs-border text-cs-muted font-mono text-sm uppercase tracking-wider px-6 py-3 hover:border-cs-ochre hover:text-cs-ochre transition-colors disabled:opacity-50" data-testid="apply-promo">
                    {promoApplied ? 'Applied!' : 'Apply'}
                  </button>
                </div>
                <p className="font-mono text-xs text-cs-muted mt-3">Try: SMILE10 for 10% off</p>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-cs-card border border-cs-border p-6 sticky top-24" data-testid="checkout-summary">
                <h2 className="font-anton text-xl uppercase text-cs-text mb-6 tracking-wide">Order Summary</h2>

                <div className="space-y-4 mb-6 max-h-60 overflow-y-auto">
                  {cart.map((item) => (
                    <div key={`${item.product_id}-${item.size}-${item.color}`} className="flex gap-3">
                      <div className="relative w-16 h-16 flex-shrink-0">
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover border border-cs-border" />
                        <span className="absolute -top-2 -right-2 w-5 h-5 bg-cs-ochre text-cs-dark text-xs font-mono font-bold flex items-center justify-center">{item.quantity}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-mono text-sm text-cs-text font-bold uppercase truncate">{item.name}</p>
                        <p className="font-mono text-xs text-cs-muted">{item.size} / {item.color}</p>
                      </div>
                      <p className="font-mono text-sm text-cs-ochre font-bold">${(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                </div>

                <div className="border-t border-cs-border pt-4 space-y-3 font-mono text-sm">
                  <div className="flex justify-between">
                    <span className="text-cs-muted">Subtotal</span>
                    <span className="text-cs-text">${cartTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-cs-muted">Shipping</span>
                    <span className="text-cs-text">{shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-cs-muted">Tax</span>
                    <span className="text-cs-text">${tax.toFixed(2)}</span>
                  </div>
                  {promoApplied && (
                    <div className="flex justify-between text-green-500">
                      <span>Discount ({promoDiscount}%)</span>
                      <span>-${discount.toFixed(2)}</span>
                    </div>
                  )}
                </div>

                <div className="border-t border-cs-border mt-4 pt-4">
                  <div className="flex justify-between items-center mb-6">
                    <span className="font-anton text-xl uppercase text-cs-text tracking-wide">Total</span>
                    <span className="font-mono text-2xl text-cs-ochre font-bold">${total.toFixed(2)}</span>
                  </div>
                  <button type="submit" disabled={loading} className="w-full bg-cs-olive text-cs-text font-mono uppercase tracking-wider text-sm py-4 hover:bg-cs-olive2 transition-colors flex items-center justify-center gap-2 disabled:opacity-50" data-testid="pay-now-btn">
                    <Lock className="w-4 h-4" />
                    {loading ? 'Processing...' : `Pay Now - $${total.toFixed(2)}`}
                  </button>
                  <p className="font-mono text-xs text-cs-muted text-center mt-4">Secure checkout powered by Stripe</p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
